import React, { useCallback, useContext, useEffect, useState, useMemo, useRef } from "react";
import {
  Checkbox,
  TextInput,
  Text,
  Highlight,
  Box,
  Group,
  Avatar,
  Divider,
  Loader,
  Skeleton,
} from "@mantine/core";
import { IconListSearch } from "@tabler/icons";
import { debounce } from "lodash"; // For debouncing input changes

import { useFiltersContextHelper } from '../useFiltersContextHelper';
import AddFilterBtn from "../AddFilterBtn";
import { useAuth0 } from "@auth0/auth0-react";
import { searchSelectedOrgs, searchSubmitters } from "../../../../Api";
import { getInitials } from "../../../../utils/getInitials";
import FilterFooter from "../FilterFooter";
import { findFiltersByPath } from "../../../../context/helpers";
import { AdvancedHighlight, getHighlightTerms } from "../../../../utils/highlightUtils";
import {useIdeaFilters} from "../../../../hooks/useIdeaFilters";
import { getOrgExtraInfoFieldsForFilter, ORG_EXTRA_INFO_FIELDS_LIMIT_FILTER_UI } from "../../../../utils/org-filter-config";
import { formatOrgFilterChipLabel } from "../../../../utils/company-filter-label";
import { CompanyFilterExtraInfoBadges } from "../../../common/CompanyFilterExtraInfoBadges";

const FetchableCheckboxFilter: any = ({
  MenuType,
  resetFilters,
  filterConfig,
  ideasFiltersConfig: ideasFiltersConfigProp,
}: {
  MenuType: "idea" | "evidence";
  filterConfig?: Record<string, unknown>;
  ideasFiltersConfig?: { idea?: Record<string, unknown>; evidence?: Record<string, unknown> };
  resetFilters: (
    MenuType: "idea" | "evidence",
    selectedParentFilter?: string | null
  ) => void;
}) => {
  const auth0 = useAuth0();
  const {
    activeFilters,
    setActiveFilters,
    selectedParentFilter,
    checkedComponents,
    setCheckedComponents,
    childFiltersValue,
    childQuery,
    setChildQuery,
    operator,
    setOpenedMenuType,
    setSelectedParentFilter
  } = useFiltersContextHelper();
  const { ideasFiltersConfig: ideasFiltersConfigFromHook } = useIdeaFilters();
  const ideasFiltersConfig = ideasFiltersConfigProp ?? ideasFiltersConfigFromHook;

  const [fetchedOptions, setFetchedOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const filterKey = filterConfig?.parent ?? selectedParentFilter ?? "";

  const selectedFilter = useMemo(() => {
    if (filterConfig) {
      return filterConfig;
    }
    if (!selectedParentFilter) {
      return undefined;
    }
    return (
      findFiltersByPath(ideasFiltersConfig, `${MenuType}.${selectedParentFilter}`) ??
      findFiltersByPath(ideasFiltersConfig, selectedParentFilter)
    );
  }, [filterConfig, ideasFiltersConfig, MenuType, selectedParentFilter]);

  const selectedHasOperations = (selectedFilter as { operations?: unknown[] })?.operations?.length > 0 || false;
  const orgExtraInfoFields = useMemo(
    () => getOrgExtraInfoFieldsForFilter(
      selectedFilter as { type?: string; dbFieldPath?: string; extraUI?: { extraInfoFields?: unknown[] } }, ideasFiltersConfig,
      ORG_EXTRA_INFO_FIELDS_LIMIT_FILTER_UI
    ),
    [selectedFilter, ideasFiltersConfig]
  );

  const renderOrgOptionLabel = (option: any) => (
    <div style={{ minWidth: 0, flex: 1 }}>
      <Text lineClamp={1}>
        <AdvancedHighlight highlight={childQuery?.[filterKey] || ""}>
          {option.name}
        </AdvancedHighlight>
      </Text>
      {option.email && (
        <Text size="xs" color="dimmed" lineClamp={1}>
          <AdvancedHighlight highlight={childQuery?.[filterKey] || ""}>
            {option.email}
          </AdvancedHighlight>
        </Text>
      )}
      <CompanyFilterExtraInfoBadges org={option} extraInfoFields={orgExtraInfoFields} />
    </div>
  );

  // Track current search query to prevent race conditions
  const currentSearchQueryRef = useRef<string>('');
  const currentSearchPageRef = useRef<number>(1);

  const isSubmitterFilter = selectedFilter?.dbFieldPath?.includes('submitterId');

  const callSearchApi = useCallback(
    (params: { query: string; ids?: string[]; page?: number; limit?: number }) => {
      if (isSubmitterFilter) {
        return searchSubmitters(params, auth0).then(r => ({
          items: r.submitters || [],
          hasMore: r.hasMore,
          totalCount: r.totalCount,
          currentPage: r.currentPage,
        }));
      }
      return searchSelectedOrgs(params, auth0).then(r => ({
        items: r.accounts || [],
        hasMore: r.hasMore,
        totalCount: r.totalCount,
        currentPage: r.currentPage,
      }));
    },
    [auth0, isSubmitterFilter]
  );

  // Create debounced search function once using useMemo to prevent recreation on every render
  const searchSelectedOrgsFn = useMemo(
    () =>
      // explicitIds: pass selected IDs directly when calling on filter-open to avoid stale
      // checkedComponents closure (state update from setCheckedComponents hasn't committed yet)
      debounce((search: string, page = 1, append = false, explicitIds?: string[]) => {
        // Update refs with current search parameters
        currentSearchQueryRef.current = search;
        currentSearchPageRef.current = page;

        const ids = explicitIds !== undefined
          ? explicitIds
          : (filterKey ? checkedComponents[filterKey] : undefined);
        
        if (page === 1) {
          setLoading(true);
          setCurrentPage(1);
        } else {
          setLoadingMore(true);
        }
        
        callSearchApi({ query: search, ids, page, limit: 15 })
          .then(({ items, hasMore, totalCount, currentPage: responsePage }) => {
            // CRITICAL: Only update state if this is still the latest search query
            // This prevents stale responses from overwriting correct results
            if (
              currentSearchQueryRef.current === search &&
              currentSearchPageRef.current === page
            ) {
              const validItems = items.filter((item: any) => item && item._id);
              
              if (page === 1 || !append) {
                setFetchedOptions(validItems);
              } else {
                setFetchedOptions(prev => [...prev, ...validItems]);
              }
              
              setHasMore(hasMore);
              setTotalCount(totalCount);
              setCurrentPage(responsePage);
              setLoading(false);
              setLoadingMore(false);
            }
          })
          .catch((e) => {
            if (
              currentSearchQueryRef.current === search &&
              currentSearchPageRef.current === page
            ) {
              setLoading(false);
              setLoadingMore(false);
              console.error("Fetching filter options failed:", e);
            }
          });
      }, 500),
    [callSearchApi, filterKey, checkedComponents]
  );

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      filterKey &&
        setChildQuery((prev) => ({
          ...prev,
          [filterKey]: newValue,
        }));
      // Reset pagination for new search
      setCurrentPage(1);
      setHasMore(false);
      searchSelectedOrgsFn(newValue, 1, false);
    },
    [filterKey, searchSelectedOrgsFn]
  );

  const handleCheckboxChange = useCallback(
    (selectedIds: any[]) => {
      filterKey &&
        setCheckedComponents((prev) => ({
          ...prev,
          [filterKey]: selectedIds,
        }));
    },
    [filterKey]
  );

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50; // 50px threshold
      
      if (isNearBottom && hasMore && !loading && !loadingMore && filterKey) {
        const nextPage = currentPage + 1;
        const currentQuery = childQuery?.[filterKey] || "";
        searchSelectedOrgsFn(currentQuery, nextPage, true);
      }
    },
    [hasMore, loading, loadingMore, currentPage, filterKey, childQuery, searchSelectedOrgsFn]
  );

  useEffect(() => {
    if (!filterKey) {
      return;
    }

    const currentFilters: any = activeFilters.find(
      (aF) => aF.parent === filterKey
    );

    if (currentFilters?.selected && currentFilters) {
      setCheckedComponents((prev) => ({
        ...prev,
        ...(currentFilters.selected
          ? {
            [filterKey]: currentFilters.selected.map((o: string) =>
              o.toLowerCase()
            ),
          }
          : {}),
      }));
    }
  }, [activeFilters, filterKey]);

  useEffect(() => {
    if (!filterKey) {
      return;
    }

    setCurrentPage(1);
    setHasMore(false);
    setFetchedOptions([]);

    const currentFilters: any = activeFilters.find(
      (aF) => aF.parent === filterKey
    );
    const preloadIds = currentFilters?.selected?.map((o: string) =>
      typeof o === 'string' ? o.toLowerCase() : String(o)
    ) || [];

    searchSelectedOrgsFn(childQuery?.[filterKey] || "", 1, false, preloadIds);

    if (currentFilters?.selected) {
      setCheckedComponents((prev) => ({
        ...prev,
        ...(currentFilters.selected
          ? {
            [filterKey]: currentFilters.selected.map((o: string) =>
              o.toLowerCase()
            ),
          }
          : {}),
      }));
    }
  }, [filterKey]);

  const filterSearchLabel = (selectedFilter as { label?: string; name?: string })?.label
    ?? (selectedFilter as { name?: string })?.name
    ?? filterKey;

  return (
    filterKey && (
      <>
        <div>
          <TextInput
            mx={5}
            radius="md"
            value={childQuery?.[filterKey] || ""}
            placeholder={`Search ${filterSearchLabel.toLocaleLowerCase()}`}
            onChange={handleInputChange}
            icon={<IconListSearch size={14.5} />}
            sx={{
              input: {
                backgroundColor: "#F9FAFB",
                ":focus": {
                  borderColor: "#b1b1b1;",
                },
              },
            }}
            rightSection={loading ? <Loader size={16}/> : undefined}
          />
        </div>
        <Box
          h={300}
          sx={{
            overflowX: "hidden",
            overflowY: "auto",
          }}
          onScroll={handleScroll}
        >
          <Checkbox.Group
            value={checkedComponents[filterKey]}
            onChange={handleCheckboxChange}
            //@ts-ignore
            orientation="vertical"
            spacing="xs"
          >

            {loading && fetchedOptions.length == 0 && Array.from({ length: 6 }).map((item, i) => (
              <Checkbox
                key={i}
                maw={260}
                disabled
                styles={{
                  body: {
                    alignItems: "center",
                  },
                }}
                pr="sm"
                pl="sm"
                py={3}
                value={i}
                label={
                  <Group noWrap>
                    <Skeleton radius="xl" w={38} h={38}/>
                    <Skeleton
                      key={i}
                      height={12}
                      width={`${Math.floor(Math.random() * (150 - 90 + 1)) + 90}px`}
                      mb={0}
                      sx={{ borderRadius: "8px" }}
                    />
                  </Group>
                  
                }
              />
            ))}

            {checkedComponents[filterKey]?.length > 0 && <>
              {fetchedOptions
                ?.filter((option: any) =>
                  checkedComponents[filterKey]?.includes(option._id)
                )
                .map((option: any) => (
                  <Checkbox
                    key={option._id}
                    maw={260}
                    styles={{
                      body: {
                        alignItems: "flex-start",
                      },
                      label: {
                        width: "100%",
                      },
                    }}
                    sx={{
                      cursor: "pointer",
                      input: {
                        ":checked": {
                          backgroundColor: "#5B60E3",
                          borderColor: "#5B60E3",
                        },
                      },
                    }}
                    pr="sm"
                    pl="sm"
                    py={3}
                    value={option._id}
                    label={
                      <Group align="flex-start" noWrap={false} sx={{ width: "100%" }}>
                        <Avatar size="md" radius="xl">
                          {getInitials(option.name)}
                        </Avatar>
                        {renderOrgOptionLabel(option)}
                      </Group>
                    }
                  />
                ))}
              <Divider my={0} />
            </>}
            
            {fetchedOptions
              ?.filter(
                (option: any) =>
                  (!checkedComponents[filterKey]?.includes(option._id) && option.name !== "All")
              )
              .map((option: any) => (
                <Checkbox
                  key={option._id}
                  maw={260}
                  styles={{
                    body: {
                      alignItems: "flex-start",
                    },
                    label: {
                      width: "100%",
                    },
                  }}
                  sx={{
                    cursor: "pointer",
                    input: {
                      ":checked": {
                        backgroundColor: "#5B60E3",
                        borderColor: "#5B60E3",
                      },
                    },
                  }}
                  pr="sm"
                  pl="sm"
                  py={3}
                  value={option._id}
                  label={
                    <Group align="flex-start" noWrap={false} sx={{ width: "100%" }}>
                      <Avatar size="md" radius="xl">
                        {getInitials(option.name)}
                      </Avatar>
                      {renderOrgOptionLabel(option)}
                    </Group>
                  }
                />
              ))}

            {/* Loading more indicator */}
            {loadingMore && (
              <Group position="center" py="sm">
                <Loader size="sm" />
                <Text size="sm" color="dimmed">Loading more...</Text>
              </Group>
            )}

            {/* End of results indicator */}
            {!hasMore && !loading && fetchedOptions.length > 0 && (
              <Group position="center" py="sm">
                <Text size="xs" color="dimmed">
                  Showing all {totalCount} results
                </Text>
              </Group>
            )}

            {/* No results indicator */}
            {!loading && fetchedOptions.length === 0 && (
              <Group position="center" py="xl">
                <Text size="sm" color="dimmed">
                  No results found
                </Text>
              </Group>
            )}
          </Checkbox.Group>
        </Box>

        {/* Sticky results counter at bottom */}
        {totalCount > 0 && (
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              backgroundColor: "white",
              borderTop: "1px solid #e9ecef",
              zIndex: 10,
            }}
            px="sm"
            py={6}
          >
            <Text  size="xs" color="dimmed" ta="left">
              {fetchedOptions.length} of {totalCount} results
              {hasMore && " (scroll for more)"}
            </Text>
          </Box>
        )}

        <FilterFooter
          onClickClear={() => resetFilters(MenuType, filterKey)}
          ApplyButton={
            <AddFilterBtn
              BtnText="Apply"
              selectedParentFilter={filterKey}
              childFiltersValue={childFiltersValue}
              disabled={
                (filterKey &&
                  childFiltersValue[filterKey]?.length === 0) ||
                (selectedHasOperations && !operator)
              }
              onClick={() => {
                const parentFilterObj = filterConfig
                  ?? findFiltersByPath(ideasFiltersConfig, `${MenuType}.${filterKey}`)
                  ?? findFiltersByPath(ideasFiltersConfig, filterKey);
                const selectedIds = checkedComponents[filterKey] || [];
                const selectedLabels = selectedIds.map((id: string) => {
                  const opt = fetchedOptions.find(
                    (o: any) => o && String(o._id).toLowerCase() === String(id).toLowerCase()
                  );
                  if (orgExtraInfoFields.length > 0 && opt) {
                    return formatOrgFilterChipLabel(opt, orgExtraInfoFields);
                  }
                  return opt?.name ?? id;
                });
                
                const idToName = Object.fromEntries(
                  selectedIds.map((id: string) => {
                    const opt = fetchedOptions.find(
                      (o: any) =>
                        o?._id &&
                        String(o._id).toLowerCase() === String(id).toLowerCase()
                    );
                    const label = orgExtraInfoFields.length > 0 && opt
                      ? formatOrgFilterChipLabel(opt, orgExtraInfoFields)
                      : opt?.name ?? id;
                    return [id, label];
                  })
                );
                const selectedFilterMap: Record<string, string> = Object.fromEntries(
                  selectedIds.map((id: string, i: number) => [id, selectedLabels[i] ?? id])
                );
                setActiveFilters((prevFilters: any) => [
                  {
                    ...parentFilterObj,
                    type: parentFilterObj?.type,
                    parent: filterKey,
                    selectedFilterMap,
                    operator: operator && operator[filterKey],
                    selectedLabels,
                    selected: selectedIds,
                    query: null,
                    options: idToName,
                  },
                  ...prevFilters.filter(
                    (prevF: any) => prevF.parent !== filterKey
                  ),
                ]);
                setOpenedMenuType(null)
                setSelectedParentFilter(null)
              }}
            />
          }
        />
      </>
    )
  );
};

export default FetchableCheckboxFilter;
